/**
 * State-machine command buttons for a menu plan, mounted in the detail
 * header through `menuPlanResource.detail.headerActions` (#1804) — the header
 * is on screen whichever tab the rail shows, so a transition never costs a
 * click to find.
 *
 * Implements the 4 plan-level lifecycle transitions on the M2 state
 * machine (approve / publish / unpublish / republish-fanout) per the
 * design doc §M2 and the API in the generated contract
 * (backend/openapi.json via `npm run spec:gen`, PR #150).
 *
 * Visibility rules (mirror the backend zod boundary at
 * `backend/src/routes/menu-planning/plans.ts`):
 *   - draft     → Approve (MENU_PLANS_APPROVE)
 *   - approved  → Publish (MENU_PLANS_PUBLISH)
 *   - published → Unpublish (MENU_PLANS_PUBLISH)
 *   - published OR approved → Republish fanout
 *     (MENU_PLANS_PUBLISH — surfaces the published / unpublished
 *     re-emit path so operators can retry the M5 Shopify Sync
 *     fanout after a soft-success `emit_failed` from publish/unpublish)
 *
 * `emit_failed` UX: the backend returns 200 with the message
 * "Transitioned but downstream fanout failed" when a subscriber
 * throws after the DB transition committed. The card surfaces a
 * persistent warning banner with a "Retry fanout" button that calls
 * republish-fanout for the same event type — operator does not need
 * to re-attempt the transition (it already happened).
 *
 * Item-level amend (M2.5 INV-M2-9) ships in a dedicated sub-PR with
 * its own window-validation dialog; intentionally NOT mixed into
 * this plan-level surface.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Send, EyeOff, RotateCcw, AlertCircle } from 'lucide-react';
import { Button } from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { useConfirm } from '@components/ui/ConfirmDialog';
import { menuPlanAPI, type MenuPlan, type FanoutItem } from '@/services';
import { queryKeys } from '@lib/queryKeys';
import { useBetterAuth } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { ApiError } from '@lib/api';

interface Props {
  plan: MenuPlan;
}

/** Discriminate a soft-failure publish/unpublish/republish result.
 *
 *  The rebuilt `/api/menu` fanout returns a per-item outcome array
 *  (`{ kind, menuItemId, ... }`); the DB transition committed but a
 *  Shopify write for that item threw when `kind === 'failed'`. There is
 *  no `emit_failed` envelope sentinel any more — the machine-readable
 *  contract is the item `kind` union. A single `failed` item means the
 *  operator should retry the fanout (idempotent republish). */
export function hasFanoutFailure(result: { items: FanoutItem[] }): boolean {
  return result.items.some((item) => item.kind === 'failed');
}

type EmitFailedBanner = {
  /** Which transition fired and left an item `failed` — drives the
   *  per-action banner copy. Retry re-runs the idempotent
   *  republish-fanout (which derives its own event type from the plan's
   *  current state, so no type is threaded through here). */
  action: 'publish' | 'unpublish';
};

export function LifecycleActionsToolbar({ plan }: Props) {
  const { t } = useTranslation();
  const { user } = useBetterAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm } = useConfirm();
  const [emitBanner, setEmitBanner] = useState<EmitFailedBanner | null>(null);

  const permissions = useMemo(() => user?.permissions ?? [], [user]);
  const can = (perm: string) => permissions.includes(perm);
  // menu-plan lifecycle actions re-gated onto `catalog:write` — the
  // backend folded the legacy `menu_plans:*` write/approve/publish keys
  // into the unified `catalog:*` module (#943).
  const canApprove = can(PERMISSIONS.CATALOG_WRITE);
  const canPublish = can(PERMISSIONS.CATALOG_WRITE);

  const invalidatePlan = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.menuPlans.detail(plan.id),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.menuPlans.lists(),
    });
  };

  const handleApiError = (e: unknown, fallbackKey: string) => {
    // The rebuilt backend returns illegal-transition / not-published as a
    // 409 with a human `message` ("Cannot publish a plan in state draft")
    // and no structured `extras` sentinel, so surface the server message
    // directly, falling back to the supplied i18n key.
    if (e instanceof ApiError) {
      toast.error(e.message || t(fallbackKey));
      return;
    }
    toast.error(t(fallbackKey));
  };

  // ── approve (draft → approved, no Shopify fanout) ──────────────
  // No `emit_failed` branch: approve fires no event, so the
  // backend's `transitionResultToHttp` for approve never produces a
  // soft-success extras envelope. If that ever changes the new
  // emit path needs explicit handling here AND a backend-side
  // banner test — symptomatic console.warn is not a detector.
  const approveMutation = useMutation({
    mutationFn: () => menuPlanAPI.approve(plan.id),
    onSuccess: () => {
      toast.success(t('menu_plan.action.approve.success'));
      setEmitBanner(null);
      invalidatePlan();
    },
    onError: (e) => handleApiError(e, 'menu_plan.action.approve.failure'),
  });

  // ── publish (approved → published, fans out to Shopify) ────────
  const publishMutation = useMutation({
    mutationFn: () => menuPlanAPI.publish(plan.id),
    onSuccess: (result) => {
      invalidatePlan();
      if (hasFanoutFailure(result)) {
        setEmitBanner({ action: 'publish' });
        toast.success(t('menu_plan.action.publish.successButFanoutFailed'));
      } else {
        setEmitBanner(null);
        toast.success(t('menu_plan.action.publish.success'));
      }
    },
    onError: (e) => handleApiError(e, 'menu_plan.action.publish.failure'),
  });

  // ── unpublish (published → approved, archives Shopify products) ─
  const unpublishMutation = useMutation({
    mutationFn: () => menuPlanAPI.unpublish(plan.id),
    onSuccess: (result) => {
      invalidatePlan();
      if (hasFanoutFailure(result)) {
        setEmitBanner({ action: 'unpublish' });
        toast.success(t('menu_plan.action.unpublish.successButFanoutFailed'));
      } else {
        setEmitBanner(null);
        toast.success(t('menu_plan.action.unpublish.success'));
      }
    },
    onError: (e) => handleApiError(e, 'menu_plan.action.unpublish.failure'),
  });

  // ── republish-fanout (idempotent retry of failed Shopify items) ─
  const republishMutation = useMutation({
    mutationFn: () => menuPlanAPI.republishFanout(plan.id),
    onSuccess: (result) => {
      invalidatePlan();
      if (hasFanoutFailure(result)) {
        // Some items still failed — keep the banner up so the operator
        // can retry again once the downstream issue clears.
        toast.success(t('menu_plan.action.republishFanout.partial'));
      } else {
        setEmitBanner(null);
        toast.success(t('menu_plan.action.republishFanout.success'));
      }
    },
    onError: (e) => handleApiError(e, 'menu_plan.action.republishFanout.failure'),
  });

  const isMutating =
    approveMutation.isPending ||
    publishMutation.isPending ||
    unpublishMutation.isPending ||
    republishMutation.isPending;

  // ── click handlers — confirm dialogs gate the destructive actions
  const onApprove = async () => {
    const ok = await confirm({
      title: t('menu_plan.action.approve.confirmTitle'),
      message: t('menu_plan.action.approve.confirmMessage', {
        weekStart: plan.week_start,
      }),
      type: 'default',
      confirmText: t('menu_plan.action.approve.confirmAction'),
      // Stable selector for Playwright: the visible label is i18n
      // (en/zh) so a locale flip in CI would break a text-based
      // locator. Same testid convention as the login form in
      // `e2e/helpers/auth.ts::login`.
      confirmTestId: 'menu-plan-approve-confirm',
    });
    if (!ok) return;
    approveMutation.mutate();
  };

  const onPublish = async () => {
    const ok = await confirm({
      title: t('menu_plan.action.publish.confirmTitle'),
      message: t('menu_plan.action.publish.confirmMessage', {
        weekStart: plan.week_start,
      }),
      type: 'warning',
      confirmText: t('menu_plan.action.publish.confirmAction'),
      confirmTestId: 'menu-plan-publish-confirm',
    });
    if (!ok) return;
    publishMutation.mutate();
  };

  const onUnpublish = async () => {
    const ok = await confirm({
      title: t('menu_plan.action.unpublish.confirmTitle'),
      message: t('menu_plan.action.unpublish.confirmMessage', {
        weekStart: plan.week_start,
      }),
      type: 'danger',
      confirmText: t('menu_plan.action.unpublish.confirmAction'),
      confirmTestId: 'menu-plan-unpublish-confirm',
    });
    if (!ok) return;
    unpublishMutation.mutate();
  };

  const onRetryFanout = () => {
    if (!emitBanner) return;
    republishMutation.mutate();
  };

  // If the operator has no relevant grant, render nothing — the
  // detail page degrades to "read-only viewer" cleanly. Hides the
  // empty toolbar instead of a permission-denied placeholder.
  const showApprove = plan.status === 'draft' && canApprove;
  const showPublish = plan.status === 'approved' && canPublish;
  const showUnpublish = plan.status === 'published' && canPublish;
  if (!showApprove && !showPublish && !showUnpublish && !emitBanner) {
    return null;
  }

  return (
    <div className="flex flex-col items-end gap-3">
      {emitBanner && (
        // Width-bounded: the header slot is a right-aligned action column
        // sharing its row with the record title, and an unbounded banner would
        // squeeze that title.
        <div className="flex max-w-sm items-start gap-3 rounded-md border border-warning/30 bg-warning/10 p-3">
          <AlertCircle className="h-5 w-5 flex-none text-warning" />
          <div className="flex-1 space-y-2">
            <p className="text-sm font-medium">
              {/* Per-action banner key — avoids `{{action}}` placeholder
               *  interpolation that produced awkward English ("The
               *  unpublish succeeded") and mixed-grammar Chinese
               *  ("撤销发布已成功"). Two keys, two complete phrases,
               *  no concatenation. */}
              {t(`menu_plan.action.${emitBanner.action}.emitFailedBanner`)}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('menu_plan.action.emitFailed.explanation')}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={onRetryFanout}
              disabled={isMutating}
              data-testid="menu-plan-republish-fanout"
            >
              <RotateCcw className="mr-1 h-4 w-4" />
              {t('menu_plan.action.republishFanout.button')}
            </Button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {showApprove && (
          <Button
            size="sm"
            onClick={onApprove}
            disabled={isMutating}
            data-testid="menu-plan-approve"
          >
            <CheckCircle2 className="mr-1 h-4 w-4" />
            {t('menu_plan.action.approve.button')}
          </Button>
        )}
        {showPublish && (
          <Button
            size="sm"
            onClick={onPublish}
            disabled={isMutating}
            data-testid="menu-plan-publish"
          >
            <Send className="mr-1 h-4 w-4" />
            {t('menu_plan.action.publish.button')}
          </Button>
        )}
        {showUnpublish && (
          <Button
            size="sm"
            variant="outline"
            onClick={onUnpublish}
            disabled={isMutating}
            data-testid="menu-plan-unpublish"
          >
            <EyeOff className="mr-1 h-4 w-4" />
            {t('menu_plan.action.unpublish.button')}
          </Button>
        )}
      </div>
    </div>
  );
}
